// Shared web-entry form primitives for the Life hubs. These give users a
// second way to add data besides SMS. Styling reuses the SAME tokens and
// input idiom as the vendored shopping quick-add (rounded controls, dryvn
// line/ink/primary vars) — no new visual language, brand out of scope.

import {
  useState,
  type ReactNode,
  type InputHTMLAttributes,
  type TextareaHTMLAttributes,
  type SelectHTMLAttributes,
} from "react";
import { Plus, X } from "lucide-react";
import { DCard } from "@/components/dryvn";

const CONTROL_CLS =
  "w-full rounded-xl border px-3 py-2 text-sm outline-none transition-colors focus:ring-2";
const CONTROL_STYLE = {
  borderColor: "var(--dryvn-line)",
  color: "var(--dryvn-ink)",
  backgroundColor: "var(--dryvn-card)",
} as const;

export function LifeField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span
        className="mb-1 block font-mono text-[10px] uppercase tracking-wider"
        style={{ color: "var(--dryvn-grey)" }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

export function LifeInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={CONTROL_CLS} style={CONTROL_STYLE} />;
}

export function LifeTextarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={CONTROL_CLS} style={CONTROL_STYLE} />;
}

export function LifeSelect(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={CONTROL_CLS} style={CONTROL_STYLE} />;
}

export function SubmitButton({
  children,
  disabled,
}: {
  children: ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="submit"
      disabled={disabled}
      className="rounded-full px-4 py-2 text-sm font-medium text-white shadow-glow transition-opacity disabled:opacity-50"
      style={{ backgroundColor: "var(--dryvn-primary)" }}
    >
      {children}
    </button>
  );
}

/**
 * A collapsible "Add …" card. Closed, it's a single pill-buttoned header;
 * open, it reveals the form. Matches the shopping quick-add affordance
 * (Plus button, primary fill). `children` receives a `close` callback so a
 * successful submit can collapse the panel.
 */
export function AddPanel({
  label,
  children,
  defaultOpen = false,
}: {
  label: string;
  children: (close: () => void) => ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <DCard>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between text-left"
      >
        <span className="font-display text-lg" style={{ color: "var(--dryvn-ink)" }}>
          {label}
        </span>
        <span
          className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-white"
          style={{ backgroundColor: "var(--dryvn-primary)" }}
        >
          {open ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
        </span>
      </button>
      {open && <div className="mt-4">{children(() => setOpen(false))}</div>}
    </DCard>
  );
}
